import { ClientDeviceStats } from '../../services/statusService';

/**
 * The per-client rows of the dashboard, grouped: a root client with its
 * agencies is one group whose `total` adds the agencies' figures to the
 * root's own. A client without agencies is a group of one. An agency whose
 * root is not in the rows (out of scope) stands as a root.
 */
export interface ClientGroup {
  root: ClientDeviceStats;
  /** Biggest fleet first. */
  agencies: ClientDeviceStats[];
  /** The root's row with the group's figures (the root's own when it has no agencies). */
  total: ClientDeviceStats;
}

const sumRows = (root: ClientDeviceStats, rows: ClientDeviceStats[]): ClientDeviceStats => {
  const byType = new Map<string, number>();
  rows.forEach((r) => r.devices_by_type.forEach((x) => byType.set(x.device_type, (byType.get(x.device_type) ?? 0) + x.count)));
  return {
    ...root,
    total_devices: rows.reduce((s, r) => s + r.total_devices, 0),
    active_devices: rows.reduce((s, r) => s + r.active_devices, 0),
    devices_needing_refresh: rows.reduce((s, r) => s + r.devices_needing_refresh, 0),
    devices_by_type: Array.from(byType, ([device_type, count]) => ({ device_type, count })).sort((a, b) => b.count - a.count),
  };
};

export const groupClientStats = (rows: ClientDeviceStats[]): ClientGroup[] => {
  const ids = new Set(rows.map((r) => r.client_id));
  const children = new Map<string, ClientDeviceStats[]>();
  const roots: ClientDeviceStats[] = [];
  rows.forEach((r) => {
    if (r.parent_id && ids.has(r.parent_id)) children.set(r.parent_id, [...(children.get(r.parent_id) ?? []), r]);
    else roots.push(r);
  });
  return roots.map((root) => {
    const agencies = (children.get(root.client_id) ?? []).sort((a, b) => b.total_devices - a.total_devices);
    return { root, agencies, total: agencies.length > 0 ? sumRows(root, [root, ...agencies]) : root };
  });
};
